import i18next, { type i18n, type InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import ICU from 'i18next-icu';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, type SupportedLocale } from './locales.js';

export interface CreateI18nOptions {
  /** Initial locale (e.g., from URL path or SSG context). */
  locale?: SupportedLocale;
  /** Default namespace (defaults to `'common'`). */
  defaultNS?: string;
  /** Namespaces to register/load up-front. */
  ns?: readonly string[];
  /** Path template for HTTP-loaded locale JSON (default: `/locales/{{lng}}/{{ns}}.json`). */
  loadPath?: string;
  /** Restrict supported locales (e.g., `ADMIN_LOCALES` for backoffice). */
  supportedLngs?: readonly SupportedLocale[];
  /** Embed resources at build time (used by tests / SSG). */
  resources?: InitOptions['resources'];
  /** Disable HTTP backend (use only static resources). */
  useHttpBackend?: boolean;
  /** Disable browser language detection (set locale explicitly). */
  detectLanguage?: boolean;
}

export function createI18n(options: CreateI18nOptions = {}): i18n {
  const instance = i18next.createInstance();
  let chain = instance.use(ICU).use(initReactI18next);

  if (options.useHttpBackend !== false && !options.resources) {
    chain = chain.use(HttpBackend);
  }

  if (options.detectLanguage !== false && !options.locale) {
    chain = chain.use(LanguageDetector);
  }

  void chain.init({
    lng: options.locale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: (options.supportedLngs ?? SUPPORTED_LOCALES) as readonly string[],
    defaultNS: options.defaultNS ?? 'common',
    ns: (options.ns ?? ['common']) as string[],
    backend: { loadPath: options.loadPath ?? '/locales/{{lng}}/{{ns}}.json' },
    detection: {
      order: ['cookie', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['cookie', 'localStorage'],
    },
    // SECURITY NOTE: escapeValue:false is safe ONLY because:
    // 1. Translations are static JSON committed to this repo (not user content).
    // 2. All callers render t() via JSX text/attribute context where React auto-escapes.
    // 3. The only `innerHTML` consumer is JSON-LD in apps/marketing-web/src/seo/SEO.tsx
    //    which post-escapes for HTML-context safety.
    // DO NOT introduce dangerouslySetInnerHTML or <Trans components> rendering of
    // translated content without explicit security review.
    interpolation: { escapeValue: false },
    resources: options.resources,
    react: { useSuspense: false },
  });

  return instance;
}
